import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Container,
  Avatar,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Save,
  Cancel,
  Close,
  Visibility,
  VisibilityOff,
  Person,
  LocationOn,
  AccountBalanceWallet,
  ShoppingBag,
  ExitToApp,
  ChevronRight,
  Store,
  VpnKey,
  SupportAgent,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Home,
  Work,
  MoreVert
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { postData, getData, putData, deleteData } from '../../Webservice/Webservice';
import { getUser, getToken, syncUser, logout, getIsBusiness } from '../../Utils/authHelper';
import { toast } from 'react-toastify';
import { indianStates } from '../../Utils/states';
import MapPicker from '../../Components/MapPicker/MapPicker';
import { MyLocation } from '@mui/icons-material';

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

// ── Flipkart/Amazon Style Sidebar Configuration ──────────────────────────────
const SIDEBAR_GROUPS = [
  {
    title: 'ACCOUNT SETTINGS',
    icon: <Person color="primary" />,
    items: [
      { key: 'business', label: 'Business Details' },
      { key: 'address', label: 'Manage Addresses' },
      { key: 'security', label: 'Login & Security' },
    ]
  },
  {
    title: 'MY STUFF',
    icon: <ShoppingBag color="primary" />,
    items: [
      { key: 'orders', label: 'My Orders', path: '/orders' },
    ]
  },
  {
    title: 'HELP & SUPPORT',
    icon: <SupportAgent color="primary" />,
    items: [
      { key: 'faqs', label: 'FAQs', path: '/faqs' },
    ]
  }
];

function Profile() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('business');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdData, setPwdData] = useState({ old: '', new: '', confirm: '' });
  const [showPwd, setShowPwd] = useState({ old: false, new: false, confirm: false });
  const [pwdSaving, setPwdSaving] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressMode, setAddressMode] = useState('list'); // 'list' or 'form'
  const [addressForm, setAddressForm] = useState({
    type: 'home', name: '', mobile: '', addressLine1: '', addressLine2: '',
    landmark: '', city: '', state: '', pincode: '', country: 'India',
    latitude: '', longitude: '', isDefault: false
  });
  const [mapOpen, setMapOpen] = useState(false);

  const [profileData, setProfileData] = useState({
    name: '', email: '', mobile: '', gender: '', dob: '', status: '',
    isVerified: false, createdAt: '',
    businessName: '', businessType: '', businessRegistrationNumber: '',
    gstNumber: '', panNumber: '',
    businessAddress: { addressLine1: '', addressLine2: '', city: '', state: '', country: '', pincode: '' },
  });
  const [snapshot, setSnapshot] = useState(null);

  const mapUserToState = useCallback((u) => {
    if (!u) return;
    return {
      name: u.name || '',
      email: u.email || '',
      mobile: u.mobile || '',
      gender: u.gender ? u.gender.toLowerCase() : '',
      dob: u.dob && !isNaN(new Date(u.dob)) ? new Date(u.dob).toISOString().split('T')[0] : '',
      status: u.status || '',
      isVerified: u.isVerified || false,
      createdAt: u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '',
      businessName: u.businessName || '',
      businessType: u.businessType || '',
      businessRegistrationNumber: u.businessRegistrationNumber || '',
      gstNumber: u.gstNumber || '',
      panNumber: u.panNumber || '',
      businessAddress: {
        addressLine1: u.businessAddress?.addressLine1 || '',
        addressLine2: u.businessAddress?.addressLine2 || '',
        city: u.businessAddress?.city || '',
        state: u.businessAddress?.state || '',
        country: u.businessAddress?.country || '',
        pincode: u.businessAddress?.pincode || '',
      },
    };
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const tok = getToken();
      const res = await getData('profile/view', null, tok ? `Bearer ${tok}` : '', false);
      if (res && res.profile) {
        setProfileData(mapUserToState(res.profile));
        syncUser(res.profile);
      } else {
        const u = getUser();
        if (u) setProfileData(mapUserToState(u));
      }
    } catch (e) {
      const u = getUser();
      if (u) setProfileData(mapUserToState(u));
    } finally {
      setLoading(false);
    }
  }, [mapUserToState]);

  const fetchAddresses = useCallback(async () => {
    setLoadingAddresses(true);
    try {
      const tok = getToken();
      const res = await getData('address/list', null, tok ? `Bearer ${tok}` : '');
      if (res && res.addresses) {
        setAddresses(res.addresses);
      }
    } catch (e) {
      console.error("Error fetching addresses:", e);
    } finally {
      setLoadingAddresses(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchAddresses();
  }, [fetchProfile, fetchAddresses]);

  const hChange = (f) => (e) => setProfileData(p => ({ ...p, [f]: e.target.value }));
  const hAddrChg = (f) => (e) => setProfileData(p => ({
    ...p, businessAddress: { ...p.businessAddress, [f]: e.target.value },
  }));
  const hUpperChg = (f) => (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setProfileData(p => ({ ...p, [f]: value }));
  };

  const startEdit = () => { setSnapshot(profileData); setIsEditing(true); };
  const cancelEdit = () => { setProfileData(snapshot); setIsEditing(false); };

  const handleSave = async () => {
    if (!profileData.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: profileData.name.trim(),
        gender: profileData.gender,
        dob: profileData.dob,
        ...(getIsBusiness() ? {
          businessName: profileData.businessName,
          businessType: profileData.businessType,
          businessRegistrationNumber: profileData.businessRegistrationNumber,
          gstNumber: profileData.gstNumber,
          panNumber: profileData.panNumber,
          businessAddress: profileData.businessAddress,
        } : {}),
      };
      const tok = getToken();
      const res = await postData('profile/update', payload, tok ? `Bearer ${tok}` : '', true, false);
      if (res && res.data) {
        syncUser(res.data);
        setProfileData(mapUserToState(res.data));
        setIsEditing(false);
        toast.success(res.message || 'Profile updated successfully!');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to update profile.');
    } finally { setSaving(false); }
  };

  const openEditModal = (field) => {
    setEditingField(field);
    setEditingValue(field === 'email' ? profileData.email : profileData.mobile);
    setOtpSent(false); setOtpValue('');
    setEditModalOpen(true);
  };
  const closeEditModal = () => {
    setEditModalOpen(false); setEditingField(null);
    setEditingValue(''); setOtpSent(false); setOtpValue('');
  };
  const sendOtpChange = async () => {
    setOtpLoading(true);
    try {
      const tok = getToken();
      await postData('profile/update/otp', { type: editingField, value: editingValue }, tok ? `Bearer ${tok}` : '');
      setOtpSent(true);
    } catch (e) { } finally { setOtpLoading(false); }
  };
  const verifyOtpChange = async () => {
    setOtpLoading(true);
    try {
      const tok = getToken();
      const res = await postData('profile/update/verifyotp', { type: editingField, otp: otpValue }, tok ? `Bearer ${tok}` : '', true, false);
      if (res && res.data) {
        setProfileData(mapUserToState(res.data));
        syncUser(res.data);
        toast.success(`${editingField} updated!`);
        closeEditModal();
      }
    } catch (e) {
      toast.error(e.message || 'Verification failed.');
    } finally { setOtpLoading(false); }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout? You will be logged out of your account!")) {
      logout();
      window.location.href = '/';
    }
  };

  const hAddrForm = (f) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setAddressForm(p => ({ ...p, [f]: val }));
  };

  const openAddAddress = () => {
    setEditingAddress(null);
    setAddressForm({
      type: 'home', name: profileData.name || '', mobile: profileData.mobile || '',
      addressLine1: '', addressLine2: '', landmark: '', city: '', state: '',
      pincode: '', country: 'India', latitude: '', longitude: '', isDefault: addresses.length === 0
    });
    setAddressMode('form');
  };

  const openEditAddress = (addr) => {
    setEditingAddress(addr);
    setAddressForm({
      type: addr.type || 'home',
      name: addr.name || '',
      mobile: addr.mobile || '',
      addressLine1: addr.addressLine1 || '',
      addressLine2: addr.addressLine2 || '',
      landmark: addr.landmark || '',
      city: addr.city || '',
      state: addr.state || '',
      pincode: addr.pincode || '',
      country: addr.country || 'India',
      latitude: addr.latitude || '',
      longitude: addr.longitude || '',
      isDefault: addr.isDefault || false
    });
    setAddressMode('form');
  };

  const handleSaveAddress = async () => {
    if (!addressForm.name || !addressForm.mobile || !addressForm.addressLine1 || !addressForm.city || !addressForm.pincode) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const tok = getToken();
      const endpoint = editingAddress ? `address/update/${editingAddress._id}` : 'address/add';
      const payload = { ...addressForm, isDefault: addressForm.isDefault.toString() };

      let res;
      if (editingAddress) {
        res = await putData(endpoint, payload, tok ? `Bearer ${tok}` : '');
      } else {
        res = await postData(endpoint, payload, tok ? `Bearer ${tok}` : '');
      }

      toast.success(editingAddress ? 'Address updated!' : 'Address added!');
      setAddressMode('list');
      fetchAddresses();
    } catch (e) {
      toast.error(e.message || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    try {
      const tok = getToken();
      await deleteData(`address/delete/${id}`, null, tok ? `Bearer ${tok}` : '');
      toast.success('Address deleted');
      fetchAddresses();
    } catch (e) {
      toast.error('Failed to delete address');
    }
  };

  const markAsDefault = async (id) => {
    try {
      const tok = getToken();
      await putData(`address/update/${id}`, { isDefault: "true" }, tok ? `Bearer ${tok}` : '');
      toast.success('Default address updated');
      fetchAddresses();
    } catch (e) {
      toast.error('Failed to update default address');
    }
  };

  const handleMapSelect = (coords) => {
    if (coords.details && coords.details.extracted) {
      const ext = coords.details.extracted;
      setAddressForm(prev => ({
        ...prev,
        addressLine1: ext.addressLine1 || prev.addressLine1,
        city: ext.city || prev.city,
        state: ext.state || prev.state,
        pincode: ext.pincode || prev.pincode,
        landmark: ext.landmark || prev.landmark,
        latitude: coords.lat.toString(),
        longitude: coords.lng.toString()
      }));
      toast.success('Address fetched from map!');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f1f3f6' }}>
        <CircularProgress sx={{ color: '#2874f0' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f3f6', pb: 10, pt: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={2}>

          {/* ── LEFT SIDEBAR (Flipkart Style) ─────────────────────────────────── */}
          <Grid item xs={12} md={3.3}>
            {/* User Hello Header */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, border: '1px solid #e0e0e0', borderRadius: '2px' }}>
              <Avatar sx={{ width: 48, height: 48, bgcolor: '#2874f0' }}>
                <Person />
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: '12px', color: '#878787', ...rubikfont }}>Hello,</Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, ...rubikfont }}>{profileData.businessName || profileData.name || 'User'}</Typography>
              </Box>
            </Paper>

            {/* Sidebar Navigation Groups */}
            <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '2px', overflow: 'hidden' }}>
              <List disablePadding>
                {SIDEBAR_GROUPS.map((group, gIdx) => (
                  <React.Fragment key={group.title}>
                    {gIdx > 0 && <Divider />}
                    <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      {group.icon}
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#878787', letterSpacing: '0.5px', ...rubikfont }}>
                        {group.title}
                      </Typography>
                    </Box>
                    {group.items.map((item) => {
                      const isActive = activeTab === item.key;
                      const itemLabel = item.key === 'business' && !getIsBusiness() ? 'Personal Details' : item.label;
                      return (
                        <ListItemButton
                          key={item.key}
                          onClick={() => {
                            if (item.path) navigate(item.path);
                            else {
                              setActiveTab(item.key);
                              setIsEditing(false);
                            }
                          }}
                          sx={{
                            pl: 7.5,
                            py: 1.5,
                            bgcolor: isActive ? 'rgba(40, 116, 240, 0.04)' : 'transparent',
                            '&:hover': { bgcolor: '#f5faff' }
                          }}
                        >
                          <ListItemText
                            primary={itemLabel}
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '14px',
                                color: isActive ? '#2874f0' : '#333',
                                fontWeight: isActive ? 600 : 400,
                                ...rubikfont
                              }
                            }}
                          />
                          {isActive && <ChevronRight sx={{ color: '#2874f0', fontSize: 18 }} />}
                        </ListItemButton>
                      );
                    })}
                  </React.Fragment>
                ))}

                <Divider />
                <ListItemButton onClick={handleLogout} sx={{ px: 2, py: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><ExitToApp sx={{ color: '#2874f0' }} /></ListItemIcon>
                  <ListItemText primary="Logout" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 600, color: '#2874f0', ...rubikfont } }} />
                </ListItemButton>
              </List>
            </Paper>
          </Grid>

          {/* ── RIGHT CONTENT PANEL (Flipkart Style) ─────────────────────────── */}
          <Grid item xs={12} md={8.7}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid #e0e0e0', borderRadius: '2px', minHeight: '600px' }}>

              {/* Context Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                  {activeTab === 'business' && !getIsBusiness() ? 'Personal Details' : SIDEBAR_GROUPS.flatMap(g => g.items).find(i => i.key === activeTab)?.label}
                </Typography>
                {(activeTab === 'profile' || activeTab === 'business') && !isEditing && (
                  <Button sx={{ color: '#2874f0', fontWeight: 600, textTransform: 'none' }} onClick={startEdit}>
                    Edit
                  </Button>
                )}
              </Box>

              {/* Form Content */}
              <Box sx={{ maxWidth: '700px' }}>


                {activeTab === 'business' && !getIsBusiness() && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Full Name" value={profileData.name} onChange={hChange('name')} disabled={!isEditing} /></Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Gender" value={profileData.gender} onChange={hChange('gender')} disabled={!isEditing}>
                        {['male', 'female', 'other'].map(v => <MenuItem key={v} value={v} sx={{ textTransform: 'capitalize' }}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Date of Birth" InputLabelProps={{ shrink: true }} value={profileData.dob} onChange={hChange('dob')} disabled={!isEditing} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={profileData.email} disabled /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Mobile" value={profileData.mobile} disabled /></Grid>
                  </Grid>
                )}

                {activeTab === 'business' && getIsBusiness() && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Business Name" value={profileData.businessName} onChange={hChange('businessName')} disabled={!isEditing} /></Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Business Type" value={profileData.businessType} onChange={hChange('businessType')} disabled={!isEditing}>
                        {['proprietorship', 'partnership', 'llp', 'private_limited', 'public_limited', 'other'].map(v => (
                          <MenuItem key={v} value={v}>{v.replace(/_/g, ' ').toUpperCase()}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Registration Number" value={profileData.businessRegistrationNumber} onChange={hChange('businessRegistrationNumber')} disabled={!isEditing} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="GST Number" value={profileData.gstNumber} onChange={hUpperChg('gstNumber')} disabled={!isEditing} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="PAN Number" value={profileData.panNumber} onChange={hUpperChg('panNumber')} disabled={!isEditing} /></Grid>
                  </Grid>
                )}

                {activeTab === 'address' && (
                  <Box>
                    {addressMode === 'list' ? (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                          <Typography sx={{ fontSize: '14px', color: '#878787', ...rubikfont }}>
                            Saved Addresses ({addresses.length})
                          </Typography>
                          <Button
                            startIcon={<Add />}
                            onClick={openAddAddress}
                            sx={{ color: '#2874f0', textTransform: 'none', fontWeight: 600, border: '1px solid #e0e0e0', borderRadius: '2px', px: 2 }}
                          >
                            ADD A NEW ADDRESS
                          </Button>
                        </Box>

                        {loadingAddresses ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress size={30} /></Box>
                        ) : (
                          <Stack spacing={2}>
                            {addresses.map((addr) => (
                              <Paper
                                key={addr._id}
                                elevation={0}
                                sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '2px', position: 'relative', '&:hover': { bgcolor: '#f9f9f9' } }}
                              >
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1 }}>
                                  <Chip
                                    label={addr.type?.toUpperCase() || 'HOME'}
                                    size="small"
                                    sx={{ bgcolor: '#f0f0f0', color: '#878787', borderRadius: '2px', fontSize: '10px', fontWeight: 600 }}
                                  />
                                  {addr.isDefault && (
                                    <Chip
                                      label="DEFAULT"
                                      size="small"
                                      color="success"
                                      sx={{ borderRadius: '2px', fontSize: '10px', fontWeight: 600, height: '20px' }}
                                    />
                                  )}
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{addr.name}</Typography>
                                  <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{addr.mobile}</Typography>
                                </Box>

                                <Typography sx={{ fontSize: '14px', color: '#333', mb: 2, maxWidth: '80%' }}>
                                  {addr.addressLine1}, {addr.addressLine2 && `${addr.addressLine2}, `}
                                  {addr.landmark && `${addr.landmark}, `}
                                  {addr.city}, {addr.state} - {addr.pincode}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 3 }}>
                                  <Button
                                    size="small"
                                    onClick={() => openEditAddress(addr)}
                                    sx={{ p: 0, minWidth: 0, color: '#2874f0', fontWeight: 600, textTransform: 'none' }}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => handleDeleteAddress(addr._id)}
                                    sx={{ p: 0, minWidth: 0, color: '#2874f0', fontWeight: 600, textTransform: 'none' }}
                                  >
                                    Delete
                                  </Button>
                                  {(addr.isDefault !== true && addr.isDefault !== "true") && (
                                    <Button
                                      size="small"
                                      onClick={() => markAsDefault(addr._id)}
                                      sx={{ p: 0, minWidth: 0, color: '#fb641b', fontWeight: 600, textTransform: 'none' }}
                                    >
                                      Set as Default
                                    </Button>
                                  )}
                                </Box>
                              </Paper>
                            ))}

                            {addresses.length === 0 && (
                              <Box sx={{ textAlign: 'center', py: 8 }}>
                                <LocationOn sx={{ fontSize: 60, color: '#e0e0e0', mb: 2 }} />
                                <Typography sx={{ color: '#878787' }}>No addresses saved yet.</Typography>
                              </Box>
                            )}
                          </Stack>
                        )}
                      </Box>
                    ) : (
                      /* Address Form */
                      <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, ...rubikfont }}>
                            {editingAddress ? 'Edit Address' : 'Add New Address'}
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<MyLocation />}
                            onClick={() => setMapOpen(true)}
                            sx={{ textTransform: 'none', borderRadius: '2px' }}
                          >
                            Pick on Map
                          </Button>
                        </Box>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Name" value={addressForm.name} onChange={hAddrForm('name')} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="10-digit mobile number" value={addressForm.mobile} onChange={hAddrForm('mobile')} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Pincode" value={addressForm.pincode} onChange={hAddrForm('pincode')} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Locality" value={addressForm.landmark} onChange={hAddrForm('landmark')} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Address Line 1 (Area and Street)" value={addressForm.addressLine1} onChange={hAddrForm('addressLine1')} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Address Line 2 (Building, Apartment, etc.)" value={addressForm.addressLine2} onChange={hAddrForm('addressLine2')} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="City/District/Town" value={addressForm.city} onChange={hAddrForm('city')} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField select fullWidth label="State" value={addressForm.state} onChange={hAddrForm('state')}>
                              {indianStates.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Latitude (Optional)" value={addressForm.latitude} onChange={hAddrForm('latitude')} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Longitude (Optional)" value={addressForm.longitude} onChange={hAddrForm('longitude')} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '14px', color: '#878787', mb: 1.5 }}>Address Type</Typography>
                            <RadioGroup row value={addressForm.type} onChange={hAddrForm('type')}>
                              <FormControlLabel value="home" control={<Radio />} label="Home" />
                              <FormControlLabel value="work" control={<Radio />} label="Work" />
                              <FormControlLabel value="other" control={<Radio />} label="Other" />
                            </RadioGroup>
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={addressForm.isDefault === true || addressForm.isDefault === "true"} 
                                  onChange={hAddrForm('isDefault')} 
                                />
                              }
                              label="Make as default address"
                            />
                          </Grid>
                        </Grid>

                        <Box sx={{ mt: 5, display: 'flex', gap: 3 }}>
                          <Button
                            variant="contained"
                            onClick={handleSaveAddress}
                            disabled={saving}
                            sx={{ bgcolor: '#2874f0', color: '#fff', px: 6, py: 1.5, borderRadius: '2px', fontWeight: 600, '&:hover': { bgcolor: '#1259cc' } }}
                          >
                            {saving ? 'SAVING...' : 'SAVE'}
                          </Button>
                          <Button onClick={() => setAddressMode('list')} sx={{ color: '#2874f0', fontWeight: 600 }}>CANCEL</Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {activeTab === 'security' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', pb: 2 }}>
                      <Box>
                        <Typography sx={{ fontSize: '14px', color: '#878787', mb: 0.5 }}>Email Address</Typography>
                        <Typography sx={{ fontWeight: 500 }}>{profileData.email}</Typography>
                      </Box>
                      <Button color="primary" onClick={() => openEditModal('email')}>Change</Button>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', pb: 2 }}>
                      <Box>
                        <Typography sx={{ fontSize: '14px', color: '#878787', mb: 0.5 }}>Mobile Number</Typography>
                        <Typography sx={{ fontWeight: 500 }}>{profileData.mobile}</Typography>
                      </Box>
                      <Button color="primary" onClick={() => openEditModal('mobile')}>Change</Button>
                    </Box>
                    <Box>
                      <Button variant="outlined" sx={{ borderRadius: '2px', textTransform: 'none' }} onClick={() => setPwdModalOpen(true)}>Change Password</Button>
                    </Box>
                  </Box>
                )}

                {isEditing && (
                  <Box sx={{ mt: 6, display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving}
                      sx={{ bgcolor: '#fb641b', color: '#fff', px: 6, py: 1.5, borderRadius: '2px', fontWeight: 600, '&:hover': { bgcolor: '#e85a15' } }}
                    >
                      {saving ? 'SAVING...' : 'SAVE'}
                    </Button>
                    <Button onClick={cancelEdit} sx={{ color: '#2874f0', fontWeight: 600 }}>Cancel</Button>
                  </Box>
                )}
              </Box>

              {/* Trust Section */}
              <Box sx={{ mt: 10 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, mb: 2 }}>FAQs</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>What happens when I update my email address (or mobile number)?</Typography>
                <Typography sx={{ fontSize: '12px', color: '#333', mb: 3 }}>Your login email id (or mobile number) changes, and you will receive all future communication on your updated email id (or mobile number).</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>When will my TVFM account be updated with the new email address (or mobile number)?</Typography>
                <Typography sx={{ fontSize: '12px', color: '#333', mb: 3 }}>It happens instantly as soon as you verify the OTP sent to your new contact information.</Typography>
              </Box>

            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      <Dialog open={editModalOpen} onClose={closeEditModal} PaperProps={{ sx: { borderRadius: '2px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Update {editingField === 'email' ? 'Email' : 'Mobile'}</DialogTitle>
        <DialogContent sx={{ minWidth: '400px', pt: 2 }}>
          {!otpSent ? (
            <TextField fullWidth label={editingField === 'email' ? 'New Email' : 'New Mobile'} value={editingValue} onChange={e => setEditingValue(e.target.value)} />
          ) : (
            <TextField fullWidth label="Enter OTP" value={otpValue} onChange={e => setOtpValue(e.target.value)} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeEditModal} sx={{ color: '#878787' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={otpSent ? verifyOtpChange : sendOtpChange}
            disabled={otpLoading}
            sx={{ bgcolor: '#2874f0', '&:hover': { bgcolor: '#1259cc' } }}
          >
            {otpLoading ? <CircularProgress size={20} color="inherit" /> : (otpSent ? 'VERIFY' : 'SEND OTP')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pwdModalOpen} onClose={() => setPwdModalOpen(false)} PaperProps={{ sx: { borderRadius: '2px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Change Password</DialogTitle>
        <DialogContent sx={{ minWidth: '400px', pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField fullWidth label="Current Password" type={showPwd.old ? 'text' : 'password'} value={pwdData.old} onChange={e => setPwdData(p => ({ ...p, old: e.target.value }))} />
          <TextField fullWidth label="New Password" type={showPwd.new ? 'text' : 'password'} value={pwdData.new} onChange={e => setPwdData(p => ({ ...p, new: e.target.value }))} />
          <TextField fullWidth label="Confirm Password" type={showPwd.confirm ? 'text' : 'password'} value={pwdData.confirm} onChange={e => setPwdData(p => ({ ...p, confirm: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPwdModalOpen(false)} sx={{ color: '#878787' }}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={() => {/* handleSavePwd */ }} sx={{ bgcolor: '#fb641b', '&:hover': { bgcolor: '#e85a15' } }}>UPDATE</Button>
        </DialogActions>
      </Dialog>
      <MapPicker
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        onSelect={handleMapSelect}
        initialLocation={addressForm.latitude ? { lat: parseFloat(addressForm.latitude), lng: parseFloat(addressForm.longitude) } : null}
      />
    </Box>
  );
}

export default Profile;
